import 'App.scss';

import { Industry } from 'APIs/industries/industries.types';
import { MerchantBusinessProfile } from 'APIs/merchants/merchants.types';
import FileViewer from 'Components/Common/FileViewer';
import React, { useCallback, useState } from 'react';

import { BusinessProfileDataView, FileList, Label, MultiColRow, SubTitle } from './styles';

type Props = {
  businessProfile: MerchantBusinessProfile | null;
  industries: Array<Industry>;
  downloadFile: (fileId: string) => Promise<{ fileUrl: string; fileType: string } | null>;
};

const BusinessProfileView = ({ businessProfile, industries, downloadFile }: Props) => {
  const [displayFileId, setDisplayFileId] = useState<string | null>(null);

  const handleFileClick = useCallback((e: React.MouseEvent<HTMLButtonElement>, fileId: string | undefined) => {
    e.preventDefault();
    if (!fileId) return;
    setDisplayFileId(fileId);
  }, []);

  const handleFileModalClose = () => {
    setDisplayFileId(null);
  };

  return (
    <>
      {businessProfile && (
        <BusinessProfileDataView>
          <MultiColRow>
            <div className="col">
              <Label className="label">Business Name</Label>
              <Label className="value">{businessProfile.businessName}</Label>
            </div>
            <div className="col">
              <Label className="label">Business Type</Label>
              <Label className="value">{businessProfile.businessType}</Label>
            </div>
            <div className="col">
              <Label className="label">Company Type</Label>
              <Label className="value">{businessProfile.companyType}</Label>
            </div>
          </MultiColRow>

          <MultiColRow>
            <div className="col">
              <Label className="label">Industry</Label>
              <Label className="value">{businessProfile.industry}</Label>
            </div>
            <div className="col">
              <Label className="label">Tax Country</Label>
              <Label className="value">{businessProfile.taxCountry}</Label>
            </div>
            <div className="col">
              <Label className="label">Tax ID</Label>
              <Label className="value">{businessProfile.taxId}</Label>
            </div>
          </MultiColRow>
          <MultiColRow>
            <div className="col">
              <Label className="label">Timezone</Label>
              <Label className="value">{businessProfile.timeZone}</Label>
            </div>
          </MultiColRow>

          <SubTitle>Branding</SubTitle>

          <MultiColRow>
            <div className="col">
              <Label className="label">Brand Name</Label>
              <Label className="value">{businessProfile.brandName}</Label>
            </div>
            <div className="col">
              <Label className="label">Website</Label>
              <Label className="value">{businessProfile.website}</Label>
            </div>
            <div className="col">
              <Label className="label">Card Statement Name</Label>
              <Label className="value">{businessProfile.cardStatementName}</Label>
            </div>
          </MultiColRow>

          <MultiColRow>
            <div className="col">
              <Label className="label">SMS Sender Name</Label>
              <Label className="value">{businessProfile.smsSenderName}</Label>
            </div>
            <div className="col">
              <Label className="label">Support Site</Label>
              <Label className="value">{businessProfile.supportSite}</Label>
            </div>
            <div className="col">
              <Label className="label">Support Email</Label>
              <Label className="value">{businessProfile.supportEmailAddress}</Label>
            </div>
          </MultiColRow>

          <SubTitle>Contact details</SubTitle>

          <MultiColRow>
            <div className="col">
              <Label className="label">Given Name</Label>
              <Label className="value">{businessProfile.contactPerson.givenName}</Label>
            </div>
            <div className="col">
              <Label className="label">Family Name</Label>
              <Label className="value">{businessProfile.contactPerson.familyName}</Label>
            </div>
            <div className="col">
              <Label className="label">Job title</Label>
              <Label className="value">{businessProfile.contactPerson.title}</Label>
            </div>
          </MultiColRow>

          <MultiColRow>
            <div className="col">
              <Label className="label">Contact email</Label>
              <Label>{businessProfile.contactEmailAddress}</Label>
            </div>
            <div className="col">
              <Label className="label">Phone Number</Label>
              <Label className="value">{businessProfile.phoneNumber}</Label>
            </div>
            <div className="col"> </div>
          </MultiColRow>

          <SubTitle>Business address</SubTitle>

          <MultiColRow>
            <div className="col">
              <Label className="label">Unit Number</Label>
              <Label className="value">{businessProfile.address.unitNumber ?? '-'}</Label>
            </div>
            <div className="col">
              <Label className="label">Street Number</Label>
              <Label className="value">{businessProfile.address.streetNumber}</Label>
            </div>
            <div className="col">
              <Label className="label">Street Name</Label>
              <Label className="value">{businessProfile.address.streetName}</Label>
            </div>
          </MultiColRow>

          <MultiColRow>
            <div className="col">
              <Label className="label">Street Type</Label>
              <Label className="value">{businessProfile.address.streetType}</Label>
            </div>
            <div className="col">
              <Label className="label">Suburb</Label>
              <Label className="value">{businessProfile.address.suburb}</Label>
            </div>
            <div className="col">
              <Label className="label">Postal Code</Label>
              <Label className="value">{businessProfile.address.postalCode}</Label>
            </div>
          </MultiColRow>

          <MultiColRow>
            <div className="col">
              <Label className="label">State</Label>
              <Label className="value">{businessProfile.address.state ?? '-'}</Label>
            </div>
            <div className="col">
              <Label className="label">City</Label>
              <Label className="value">{businessProfile.address.city ?? '-'}</Label>
            </div>
            <div className="col">
              <Label className="label">Country</Label>
              <Label className="value">{businessProfile.address.country}</Label>
            </div>
          </MultiColRow>

          <SubTitle>Business verification documents</SubTitle>

          <MultiColRow>
            <div className="col">
              <Label className="label">Partnership agreement</Label>
              <Label className="value">
                {businessProfile.verificationDocs.partnershipAgreementFileId ? (
                  <FileList>
                    <li>
                      <button
                        type="button"
                        className="fileId"
                        onClick={(e) => handleFileClick(e, businessProfile.verificationDocs.partnershipAgreementFileId)}
                      >
                        {businessProfile.verificationDocs.partnershipAgreementFileId}
                      </button>
                    </li>
                  </FileList>
                ) : (
                  <span className="pending">-- Pending --</span>
                )}
              </Label>
            </div>
            <div className="col">
              <Label className="label">Trust deed</Label>
              <Label className="value">
                {businessProfile.verificationDocs.trustDeedFileId ? (
                  <FileList>
                    <li>
                      <button
                        type="button"
                        className="fileId"
                        onClick={(e) => handleFileClick(e, businessProfile.verificationDocs.trustDeedFileId)}
                      >
                        {businessProfile.verificationDocs.trustDeedFileId}
                      </button>
                    </li>
                  </FileList>
                ) : (
                  <span className="pending">-- Pending --</span>
                )}
              </Label>
            </div>
            <div className="col">
              <Label className="label">Trust deed amendments</Label>
              <Label className="value">
                {businessProfile.verificationDocs.trustDeedAmendmentFileIds ? (
                  <FileList>
                    {businessProfile.verificationDocs.trustDeedAmendmentFileIds.map((fileId) => (
                      <li key={fileId}>
                        <button className="fileId" onClick={(e) => handleFileClick(e, fileId)}>
                          {fileId}
                        </button>
                      </li>
                    ))}
                  </FileList>
                ) : (
                  <span className="pending">-- Pending --</span>
                )}
              </Label>
            </div>
          </MultiColRow>
          <FileViewer
            displayFileId={displayFileId}
            downloadFile={downloadFile}
            handleFileModalClose={handleFileModalClose}
          ></FileViewer>
        </BusinessProfileDataView>
      )}
    </>
  );
};

export default BusinessProfileView;
